import DynamicForm from '@remote-com/mushu/src/components/Form/DynamicForm';
import { SuccessTitle, SuccessActions } from '@remote-com/mushu/src/components/Success';
import SuccessMark from '@remote-com/mushu/src/components/Success/SuccessMark';
import { GlobalStyles, theme as mushuTheme } from '@remote-com/mushu/src/config/styles';
import { useFormEvents } from '@remote-com/mushu/src/hooks/useFormEvents';
import { useRouter } from 'next/router';
import { useRef, useState, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button, ButtonInline } from 'remote-com-shared/components/Button';
import { dataLayerPush } from 'remote-com-shared/components/CookieScripts/helpers';
import Modal from 'remote-com-shared/components/ModalForm';
import {
  getCurrentURL,
  registerLead,
  createPartnerStackSignup,
} from 'remote-com-shared/components/ModalForm/helpers/services';
import {
  FormContainer,
  SubmitButton,
  CaptchaWrapper,
  SubsribeSuccessContainer,
  ModalTitle,
  FormError,
  DynamicFormWrapper,
  SubmitButtonWrapper,
  ModalDescription,
} from 'remote-com-shared/components/ModalForm/styles';
import { getFields } from 'remote-com-shared/domains/hubspot/fields';
import { handleFormRedirect } from 'remote-com-shared/domains/hubspot/helpers';
import { useHubspotForm } from 'remote-com-shared/domains/hubspot/hooks';
import { hasImpactFields } from 'remote-com-shared/domains/impact/helpers';
import { LocaleContextConsumer } from 'remote-com-shared/domains/locales/context';
import { useImpactFormValues } from 'remote-com-shared/domains/shared/trackingParameters';
import { getPartnerStackInfo } from 'remote-com-shared/utils/partnerstack';
import styled, { ThemeProvider } from 'styled-components';
import { v4 as uuid4 } from 'uuid';

import { SuccessMessage } from '@/src/views/DownloadableResources/Form';

export function FormComponent({
  hubspotFormDefinition,
  modalFormId,
  modalFormTitle,
  modalFormDescription,
  modalFormSubmitLabel,
  inModal = false,
  gatedFile,
  gatedFileDownloadLabel,
}) {
  const formId = modalFormId || hubspotFormDefinition?.guid;

  const portalId = hubspotFormDefinition?.portalId || '7405301';

  const {
    isLoading,
    submitText = '',
    inlineMessage: successMessage = '',
    formFieldGroups = [],
    hasRedirect,
    redirect,
  } = useHubspotForm(formId);

  const [showLoader, setShowLoader] = useState(false);
  const [showFormError, setShowFormError] = useState(false);
  const [isCaptchaDone, setIsCaptchaDone] = useState(false);
  const [showSuccessState, setShowSuccessState] = useState(false);

  const { formState } = useFormEvents();
  const [formValues, setFormValues] = useState({});

  const reCaptchaRef = useRef(null);
  const impactFormValues = useImpactFormValues();
  const fields = getFields(formFieldGroups);

  function handleCaptchaChange(value) {
    setIsCaptchaDone(value);
  }

  async function handleSubmit(values, { setSubmitting }) {
    setShowLoader(true);

    const updatedFormValues = {
      ...formValues,
      ...values,
    };
    setFormValues(updatedFormValues);

    if (isCaptchaDone) {
      const includePartnerstack =
        Object.prototype.hasOwnProperty.call(values, 'pspartnerkey') &&
        Object.prototype.hasOwnProperty.call(values, 'pspartner_group');
      const includeImpactValues = hasImpactFields(values);

      try {
        if (includePartnerstack) {
          try {
            const publicSlug = uuid4();
            await createPartnerStackSignup({
              name: values.name,
              email: publicSlug,
            });
          } catch {
            // Ignore as failing should not break hubspot
          }
        }

        const payload = {
          ...values,
          ...(Object.prototype.hasOwnProperty.call(values, 'utm')
            ? {
                utm: getCurrentURL(),
              }
            : {}),
          ...(includePartnerstack ? getPartnerStackInfo() : {}),
          ...(includeImpactValues ? impactFormValues : {}),
        };

        await registerLead(
          payload,
          `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}/`
        );

        dataLayerPush({
          event: 'user-submitted-hubspot-form',
          label: window.location.pathname,
          conversion: { send_to: 'AW-594140685/SLovCLa8nOEBEI28p5sC' },
        });

        setShowLoader(false);
        setShowSuccessState(true);

        if (hasRedirect) {
          handleFormRedirect(redirect);
        }
      } catch (exception) {
        reCaptchaRef.current.reset();
        setIsCaptchaDone(false);
        setShowFormError(true);
        setShowLoader(false);
        setSubmitting(false);

        throw exception;
      }
    }
  }

  return (
    <ThemeProvider theme={{ mode: 'light', ...mushuTheme }}>
      <GlobalStyles />
      <FormContainer>
        {showSuccessState ? (
          <SubsribeSuccessContainer>
            <SuccessMark />
            <SuccessTitle>Thank you!</SuccessTitle>
            <SuccessMessage dangerouslySetInnerHTML={{ __html: successMessage }} />
            {hasRedirect && <SuccessMessage>Redirecting…</SuccessMessage>}
            <SuccessActions>
              {gatedFile && (
                <Button href={gatedFile.url} target={gatedFile.target}>
                  {gatedFileDownloadLabel}
                </Button>
              )}
            </SuccessActions>
          </SubsribeSuccessContainer>
        ) : (
          <DynamicFormWrapper>
            {modalFormTitle && <ModalTitle>{modalFormTitle}</ModalTitle>}

            {modalFormDescription && <ModalDescription>{modalFormDescription}</ModalDescription>}

            {showFormError && (
              <FormError>
                Something went wrong. Please try again, or contact{' '}
                <ButtonInline href="mailto:help@remote.com">help@remote.com</ButtonInline> if this
                error persists. Thank you.
              </FormError>
            )}
            {isLoading ? (
              <SubmitButtonWrapper>
                <SubmitButton isLoading={isLoading}>Loading</SubmitButton>
              </SubmitButtonWrapper>
            ) : (
              <>
                <DynamicForm
                  formName={`${formId}${inModal ? '-modal' : ''}`}
                  fields={fields}
                  fieldValues={formValues}
                  formError={formState.errorMessage}
                  formTheme="dark"
                  formSize="small"
                  handleOnSubmit={handleSubmit}
                  isLoading={formState.isLoading}
                  enableReinitialize
                />

                <CaptchaWrapper>
                  <LocaleContextConsumer>
                    {(locale) => (
                      <ReCAPTCHA
                        hl={locale}
                        ref={reCaptchaRef}
                        sitekey={process.env.NEXT_PUBLIC_CAPTCHA_KEY}
                        onChange={handleCaptchaChange}
                      />
                    )}
                  </LocaleContextConsumer>
                </CaptchaWrapper>

                <SubmitButtonWrapper>
                  <SubmitButton
                    isLoading={showLoader}
                    disabled={!isCaptchaDone}
                    form={`${formId}${inModal ? '-modal' : ''}`}
                  >
                    {modalFormSubmitLabel || submitText}
                  </SubmitButton>
                </SubmitButtonWrapper>
              </>
            )}
          </DynamicFormWrapper>
        )}
      </FormContainer>
    </ThemeProvider>
  );
}

export const SimpleFormModal = ({ visible, onCancel, ...props }) => (
  <Modal visible={visible} width="660px" footer={null} onCancel={onCancel}>
    <FormComponent {...props} inModal />
  </Modal>
);

export const CTA = styled(Button).attrs({
  tone: 'primary',
  shadow: true,
})``;

export const SimpleFormModalButton = ({
  startOpenViaUrl,
  formName,
  openModal,
  className,
  ...props
}) => {
  const router = useRouter();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (openModal) setShowModal(true);

    if (startOpenViaUrl) {
      // If formName exists as url parameter with a truthy value
      setShowModal(Boolean(router.query[formName]));
    }
  }, [openModal, router, formName, startOpenViaUrl]);

  function showDemoModal() {
    setShowModal(true);
  }
  function hideDemoModal() {
    setShowModal(false);
  }

  // Ensures any other props are correctly provided if its a styled component
  const { as: buttonAlias, ...buttonProps } = props.button || {};
  const CTAComponent = buttonAlias || CTA;

  return (
    <>
      <CTAComponent onClick={showDemoModal} className={className} {...(buttonProps || {})}>
        {props.children}
      </CTAComponent>
      <SimpleFormModal visible={showModal} onCancel={hideDemoModal} {...props} />
    </>
  );
};
